import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AuthUser } from '@/api/types/auth.types';

/**
 * SECURITY NOTE:
 * - The access token is stored in memory only (not persisted to localStorage/sessionStorage) -
 *   this protects against XSS attacks that could steal it from storage.
 * - Only basic user profile info is persisted (no sensitive data).
 * - The refresh token is never held here at all - see AuthController's SetRefreshTokenCookie: it
 *   lives only in an httpOnly cookie the browser manages, which JS can't read even if XSS'd, and
 *   which survives page reloads and browser restarts (up to its own 7-day lifetime) on its own.
 *   App.tsx's bootstrap effect uses that cookie to silently re-authenticate on load.
 */

interface AuthState {
  // State
  user: AuthUser | null;
  token: string | null;       // In-memory only, NOT persisted
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: AuthUser) => void;
  setTokens: (token: string) => void;
  login: (user: AuthUser, token: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  clearTokens: () => void;

  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

// Keeps the in-memory access token in sync across same-origin tabs. Before the refresh token
// moved to an httpOnly cookie, this also had to relay refresh-token rotation (a tab that refreshed
// while a sibling tab still held the now-revoked pair would trigger the backend's reuse-attack
// detection and log every open tab out at once - see AuthService.RefreshTokenAsync). The cookie is
// now the single browser-managed source of truth for that, so this channel only needs to keep the
// short-lived access token itself from drifting between tabs.
const AUTH_CHANNEL_NAME = 'tcy-auth-sync';
const authChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;

type AuthBroadcastMessage =
  | { type: 'tokens-updated'; token: string }
  | { type: 'login'; user: AuthUser; token: string }
  | { type: 'logout' };

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,

      // Actions
      setUser: (user) => set({ user }),

      setTokens: (token) => {
        set({ token });
        authChannel?.postMessage({ type: 'tokens-updated', token } satisfies AuthBroadcastMessage);
      },

      login: (user, token) => {
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
        authChannel?.postMessage({ type: 'login', user, token } satisfies AuthBroadcastMessage);
      },

      logout: () => {
        // Clear token from memory
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
        // Clear persisted storage
        sessionStorage.removeItem('tcy-auth-storage');
        authChannel?.postMessage({ type: 'logout' } satisfies AuthBroadcastMessage);
      },

      setLoading: (loading) => set({ isLoading: loading }),

      clearTokens: () => set({ token: null }),

      // Permission helpers
      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;
        return user.permissions?.includes(permission) || user.role === 'Admin';
      },

      hasRole: (role) => {
        const { user } = get();
        if (!user) return false;
        return user.role === role || user.role === 'Admin';
      },
    }),
    {
      name: 'tcy-auth-storage',
      // Use sessionStorage instead of localStorage (cleared when browser closes)
      storage: createJSONStorage(() => sessionStorage),
      // SECURITY: Only persist non-sensitive user profile data
      // The token is NOT persisted - it stays in memory only
      partialize: (state) => ({
        user: state.user ? {
          id: state.user.id,
          email: state.user.email,
          fullName: state.user.fullName,
          firstName: state.user.firstName,
          lastName: state.user.lastName,
          role: state.user.role,
          tenantId: state.user.tenantId,
          permissions: state.user.permissions,
          locale: state.user.locale,
          timezone: state.user.timezone,
        } : null,
        // Note: isAuthenticated will be false on page reload
        // User must re-authenticate or use silent refresh via httpOnly cookie
      }),
      // On rehydration the token is always null (never persisted). isLoading is deliberately left
      // at its initial `true` (except for the E2E branch, which restores a complete session
      // synchronously) so ProtectedRoute keeps showing a spinner until App.tsx's bootstrap effect
      // has had a chance to try a silent refresh against the httpOnly cookie and either restore the
      // session or give up and flip isLoading false itself via logout().
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Check for E2E test auth state in localStorage (Playwright stores state there).
          // This allows E2E tests to persist authentication across test files - but it must
          // never run in a production build: import.meta.env.DEV is statically false (and this
          // whole block dead-code-eliminated) in a prod bundle, so a value written to this
          // localStorage key (leftover, a browser extension, or manual tampering) can no longer
          // be trusted to silently grant an authenticated session/role.
          if (import.meta.env.DEV) {
            const e2eAuth = localStorage.getItem('tcy-auth-storage');
            if (e2eAuth) {
              try {
                const parsed = JSON.parse(e2eAuth);
                if (parsed.state?.token && parsed.state?.isAuthenticated) {
                  state.token = parsed.state.token;
                  state.isAuthenticated = true;
                  state.user = parsed.state.user;
                  state.isLoading = false;
                  return;
                }
              } catch {
                // Ignore parse errors
              }
            }
          }

          // Normal flow: the token is not persisted. Leave isAuthenticated/token as their initial
          // values and isLoading true - App.tsx's bootstrap effect takes it from here.
          state.isAuthenticated = false;
          state.token = null;
        }
      },
    }
  )
);

// Apply token updates broadcast by a sibling tab (see the BroadcastChannel comment above) -
// only via setState, never through the login/setTokens/logout actions themselves, so this never
// re-broadcasts and echoes back to the tab that originated the change.
if (authChannel) {
  authChannel.onmessage = (event: MessageEvent<AuthBroadcastMessage>) => {
    const message = event.data;
    if (message.type === 'tokens-updated') {
      useAuthStore.setState({ token: message.token });
    } else if (message.type === 'login') {
      useAuthStore.setState({
        user: message.user,
        token: message.token,
        isAuthenticated: true,
        isLoading: false,
      });
    } else if (message.type === 'logout') {
      useAuthStore.setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  };
}
